import ExcelJS from "exceljs";
import {
  GOLF_RETAIL_ITEM_TYPES,
  SPA_RETAIL_ITEM_TYPES,
  getRetailItemTypes,
} from "../../constants/RetailItemTypes";
import { ProductEnum } from "../../constants/login";
import { ExcelEmptyDefault } from "../constants/defaults";
import {
  RetailItemExcelHeader,
  RetailItemExcelSourceField,
} from "../enums/retailItemExcel";
import { retailItemExcelColumn } from "../enums/typedExcel";
import { ExcelExportColumn } from "../types";
import { isBooleanColumnDefault, isNumericColumnDefault } from "./payloadRows";

const RETAIL_ITEM_WORKSHEET_NAME = "RetailItem";
const ITEM_TYPES_WORKSHEET_NAME = "ItemTypes";

/** Headers that must be filled for import (yellow in the download template). */
const MANDATORY_RETAIL_ITEM_IMPORT_HEADERS = new Set([
  RetailItemExcelHeader.OutletCode,
  RetailItemExcelHeader.ItemNumber,
  RetailItemExcelHeader.ItemDescription,
  "Sales Price",
  RetailItemExcelHeader.Category,
  RetailItemExcelHeader.ItemType,
]);

/**
 * Columns shown on Retail Items Setup (Golf/Spa) — used for download template + import only.
 * Full export still uses retailItemExcelColumns in retailItemRows.ts.
 */
export const retailItemScreenImportColumns: ExcelExportColumn[] = [
  retailItemExcelColumn(
    RetailItemExcelHeader.OutletCode,
    RetailItemExcelSourceField.OutletCode,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.ItemNumber,
    RetailItemExcelSourceField.ItemNumber,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.ItemDescription,
    RetailItemExcelSourceField.ItemDescription,
  ),
  // Screen label is "Sales Price"; accept export header "Sales price" when reading.
  {
    header: "Sales Price",
    sourceField: RetailItemExcelSourceField.SalesPrice,
  },
  retailItemExcelColumn(
    RetailItemExcelHeader.Quantity,
    RetailItemExcelSourceField.Quantity,
    ExcelEmptyDefault.Empty,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.Category,
    RetailItemExcelSourceField.Category,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.ItemType,
    RetailItemExcelSourceField.ItemType,
  ),
];

const DEFAULT_RETAIL_ITEM_VALUES: Record<string, unknown> = {
  [RetailItemExcelSourceField.SubCategory1]: "",
  [RetailItemExcelSourceField.SubCategory2]: "",
  [RetailItemExcelSourceField.SubCategory3]: "",
  [RetailItemExcelSourceField.SubCategory4]: "",
  [RetailItemExcelSourceField.SubCategory5]: "",
  [RetailItemExcelSourceField.UnitOfMeasure]: "",
  [RetailItemExcelSourceField.SecondaryBarCode]: "",
  [RetailItemExcelSourceField.Tax]: "",
  [RetailItemExcelSourceField.CommissionPercent]: 0,
  [RetailItemExcelSourceField.CommissionableItem]: 0,
  [RetailItemExcelSourceField.CommissionRequired]: 0,
  [RetailItemExcelSourceField.UseInventory]: 0,
  [RetailItemExcelSourceField.GroupingKey]: 0,
  [RetailItemExcelSourceField.OpenItems]: 0,
  [RetailItemExcelSourceField.Multipacks]: 0,
  [RetailItemExcelSourceField.Gratuity]: 0,
  [RetailItemExcelSourceField.ServiceCharge]: 0,
  [RetailItemExcelSourceField.UnitCost]: 0,
  [RetailItemExcelSourceField.ItemPar]: 0,
  [RetailItemExcelSourceField.OutletSwitch]: 0,
  [RetailItemExcelSourceField.AllowEarn]: 0,
};

const cellToPrimitive = (value: ExcelJS.CellValue): unknown => {
  if (value == null) {
    return "";
  }
  if (value instanceof Date) {
    return value.toISOString().split("T")[0];
  }
  if (typeof value === "object") {
    if ("result" in value && value.result != null) {
      return value.result;
    }
    if ("text" in value && value.text != null) {
      return value.text;
    }
    if ("richText" in value && Array.isArray(value.richText)) {
      return value.richText.map((part) => part.text ?? "").join("");
    }
  }
  return value;
};

const parseBoolean = (value: unknown, fallback = false): boolean => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value !== 0;
  }
  if (typeof value === "string") {
    const trimmed = value.trim().toLowerCase();
    if (["true", "yes", "1"].includes(trimmed)) {
      return true;
    }
    if (["false", "no", "0", ""].includes(trimmed)) {
      return false;
    }
  }
  return fallback;
};

const parseNumber = (value: unknown, fallback = 0): number | string => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return fallback;
    }
    const parsed = Number(trimmed);
    if (!Number.isNaN(parsed) && Number.isFinite(parsed)) {
      return parsed;
    }
    return trimmed;
  }
  return fallback;
};

const isBlankCell = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return String(value).trim() === "";
};

const isRowEmpty = (row: Record<string, unknown>): boolean =>
  Object.values(row).every(isBlankCell);

/** Skip leftover Excel rows that have no retail-item identity. */
const isRetailItemDataRow = (row: Record<string, unknown>): boolean => {
  const itemNumber = row[RetailItemExcelHeader.ItemNumber];
  const itemDescription = row[RetailItemExcelHeader.ItemDescription];
  const category = row[RetailItemExcelHeader.Category];
  const outletCode =
    row[RetailItemExcelHeader.OutletCode] ?? row.Outlet ?? row["Outlet Name"];
  const itemType = row[RetailItemExcelHeader.ItemType];
  const salesPrice =
    row["Sales Price"] ?? row[RetailItemExcelHeader.SalesPrice];

  return (
    !isBlankCell(itemNumber) ||
    !isBlankCell(itemDescription) ||
    !isBlankCell(category) ||
    !isBlankCell(outletCode) ||
    !isBlankCell(itemType) ||
    !isBlankCell(salesPrice)
  );
};

const getRetailItemImportColumns = (): ExcelExportColumn[] =>
  retailItemScreenImportColumns;

const readCellForColumn = (
  exportRow: Record<string, unknown>,
  column: ExcelExportColumn,
): unknown => {
  if (exportRow[column.header] !== undefined) {
    return exportRow[column.header];
  }
  // Compatibility: export workbook uses "Sales price"
  if (
    column.sourceField === RetailItemExcelSourceField.SalesPrice &&
    exportRow[RetailItemExcelHeader.SalesPrice] !== undefined
  ) {
    return exportRow[RetailItemExcelHeader.SalesPrice];
  }
  if (
    column.sourceField === RetailItemExcelSourceField.OutletCode &&
    (exportRow.Outlet !== undefined || exportRow["Outlet Name"] !== undefined)
  ) {
    return exportRow.Outlet ?? exportRow["Outlet Name"];
  }
  return undefined;
};

const mapExportRowToOnboardingRow = (
  exportRow: Record<string, unknown>,
  columns: ExcelExportColumn[],
): Record<string, unknown> => {
  const record: Record<string, unknown> = { ...DEFAULT_RETAIL_ITEM_VALUES };

  for (const column of columns) {
    const fieldKey = column.sourceField ?? column.header;
    if (!fieldKey || fieldKey === ExcelEmptyDefault.Empty) {
      continue;
    }

    const raw = readCellForColumn(exportRow, column);
    if (raw === "" || raw === null || raw === undefined) {
      continue;
    }

    if (
      isBooleanColumnDefault(column.defaultValue) ||
      typeof column.defaultValue === "boolean"
    ) {
      record[fieldKey] = parseBoolean(raw, Boolean(column.defaultValue));
      continue;
    }

    if (isNumericColumnDefault(column.defaultValue)) {
      record[fieldKey] = parseNumber(raw, column.defaultValue);
      continue;
    }

    if (
      fieldKey === RetailItemExcelSourceField.ItemNumber ||
      fieldKey === RetailItemExcelSourceField.SalesPrice ||
      fieldKey === RetailItemExcelSourceField.Quantity
    ) {
      const parsed = parseNumber(raw, Number.NaN);
      record[fieldKey] =
        typeof parsed === "number" && !Number.isNaN(parsed)
          ? parsed
          : String(raw).trim();
      continue;
    }

    record[fieldKey] = String(raw).trim();
  }

  const itemNumber = record[RetailItemExcelSourceField.ItemNumber];
  record[RetailItemExcelSourceField.BarCode] = itemNumber ?? "";

  const outletCode =
    record[RetailItemExcelSourceField.OutletCode] ??
    record[RetailItemExcelSourceField.Outlet] ??
    record[RetailItemExcelSourceField.OutletName];
  if (
    outletCode !== undefined &&
    outletCode !== null &&
    String(outletCode).trim() !== ""
  ) {
    record[RetailItemExcelSourceField.OutletCode] = String(outletCode).trim();
  }

  return record;
};

const readWorksheetRows = (
  worksheet: ExcelJS.Worksheet,
): Record<string, unknown>[] => {
  const headerRow = worksheet.getRow(1);
  const headers: string[] = [];
  headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const header = String(cellToPrimitive(cell.value) ?? "").trim();
    if (header) {
      headers[colNumber] = header;
    }
  });

  if (headers.filter(Boolean).length === 0) {
    return [];
  }

  const rows: Record<string, unknown>[] = [];
  worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber === 1) {
      return;
    }

    const exportRow: Record<string, unknown> = {};
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const header = headers[colNumber];
      if (!header) {
        return;
      }
      exportRow[header] = cellToPrimitive(cell.value);
    });

    if (!isRowEmpty(exportRow) && isRetailItemDataRow(exportRow)) {
      rows.push(exportRow);
    }
  });

  return rows;
};

export type RetailItemImportResult = {
  rows: Record<string, unknown>[];
  errors: string[];
};

/** Parses a Retail Item .xlsx (export template format) into onboarding rows. */
export const parseRetailItemsExcelFile = async (
  file: File,
): Promise<RetailItemImportResult> => {
  const columns = getRetailItemImportColumns();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());

  const worksheet =
    workbook.getWorksheet(RETAIL_ITEM_WORKSHEET_NAME) ?? workbook.worksheets[0];

  if (!worksheet) {
    return {
      rows: [],
      errors: ["The Excel file does not contain any worksheets."],
    };
  }

  const exportRows = readWorksheetRows(worksheet);
  if (exportRows.length === 0) {
    return {
      rows: [],
      errors: [
        "No data rows found. Use the Retail Item import template from this screen.",
      ],
    };
  }

  const errors: string[] = [];
  const mappedRows = exportRows.map((exportRow) =>
    mapExportRowToOnboardingRow(exportRow, columns),
  );

  mappedRows.forEach((row, index) => {
    const itemNumber = String(
      row[RetailItemExcelSourceField.ItemNumber] ?? "",
    ).trim();
    const itemDescription = String(
      row[RetailItemExcelSourceField.ItemDescription] ?? "",
    ).trim();
    const category = String(
      row[RetailItemExcelSourceField.Category] ?? "",
    ).trim();
    const outletCode = String(
      row[RetailItemExcelSourceField.OutletCode] ?? "",
    ).trim();
    const itemType = String(
      row[RetailItemExcelSourceField.ItemType] ?? "",
    ).trim();
    const salesPriceRaw = row[RetailItemExcelSourceField.SalesPrice];

    if (!itemNumber) {
      errors.push(`Row ${index + 2}: Item Number is required.`);
    } else if (Number.isNaN(Number(itemNumber))) {
      errors.push(`Row ${index + 2}: Item Number must be numeric.`);
    }

    if (!itemDescription) {
      errors.push(`Row ${index + 2}: Item Description is required.`);
    }
    if (!category) {
      errors.push(`Row ${index + 2}: Category is required.`);
    }
    if (!outletCode) {
      errors.push(`Row ${index + 2}: Outlet Code is required.`);
    }
    if (!itemType) {
      errors.push(`Row ${index + 2}: Item Type is required.`);
    }
    if (
      salesPriceRaw === undefined ||
      salesPriceRaw === null ||
      String(salesPriceRaw).trim() === ""
    ) {
      errors.push(`Row ${index + 2}: Sales Price is required.`);
    } else if (Number.isNaN(Number(salesPriceRaw))) {
      errors.push(`Row ${index + 2}: Sales Price must be numeric.`);
    }
  });

  if (errors.length > 0) {
    return { rows: [], errors };
  }

  const rows = mappedRows.map((row, index) => ({
    id: index + 1,
    ...row,
  }));

  return { rows, errors: [] };
};

/** Downloads a Retail Item template with only Retail Items Setup screen fields. */
export const downloadRetailItemImportTemplate = async (
  productType?: ProductEnum | string | null,
): Promise<void> => {
  const columns = getRetailItemImportColumns();
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(RETAIL_ITEM_WORKSHEET_NAME);

  worksheet.columns = columns.map((column) => ({
    header: column.header,
    key: column.header,
    width: 18,
  }));

  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const header = String(columns[colNumber - 1]?.header ?? "").trim();
    const isMandatory = MANDATORY_RETAIL_ITEM_IMPORT_HEADERS.has(header);
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: isMandatory ? "FFFFFF00" : "FFE0E0E0" },
    };
    if (isMandatory) {
      cell.note = "Mandatory field";
    }
  });

  const itemTypesSheet = workbook.addWorksheet(ITEM_TYPES_WORKSHEET_NAME);
  itemTypesSheet.columns = [
    { header: "Item Type Number", key: "number", width: 18 },
    { header: "Meaning", key: "meaning", width: 28 },
    { header: "Product", key: "product", width: 14 },
  ];
  const itemTypesHeader = itemTypesSheet.getRow(1);
  itemTypesHeader.font = { bold: true };
  itemTypesHeader.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFE0E0E0" },
  };

  const typeRows =
    productType === ProductEnum.GOLF || productType === ProductEnum.SPA
      ? getRetailItemTypes(productType).map((item) => ({
          number: item.value,
          meaning: item.label,
          product: String(productType),
        }))
      : [
          ...SPA_RETAIL_ITEM_TYPES.map((item) => ({
            number: item.value,
            meaning: item.label,
            product: ProductEnum.SPA,
          })),
          ...GOLF_RETAIL_ITEM_TYPES.map((item) => ({
            number: item.value,
            meaning: item.label,
            product: ProductEnum.GOLF,
          })),
        ];

  typeRows.forEach((row) => itemTypesSheet.addRow(row));

  itemTypesSheet.addRow([]);
  itemTypesSheet.addRow([
    "Use the Item Type Number in the RetailItem sheet Item Type column.",
  ]);
  itemTypesSheet.mergeCells(
    itemTypesSheet.rowCount,
    1,
    itemTypesSheet.rowCount,
    3,
  );
  itemTypesSheet.getRow(itemTypesSheet.rowCount).font = { italic: true };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "Retail Item.xlsx";
  link.click();
  URL.revokeObjectURL(url);
};
